import React, { useContext, useEffect, useReducer, useState, createContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import { TextField, InputAdornment } from "@material-ui/core";
import { normalizeSearch } from "../components/AdminWorkspace";
import Typography from "@material-ui/core/Typography";


import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";
import { canManageTenants } from "../utils/tenant";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import useHotspotStatus from "../hooks/useHotspotStatus";
import { i18n } from "../translate/i18n";

import useCompanySettings from "../hooks/useSettings/companySettings";
import { getTokens } from "../styles/tokens";
import { GROUP_ROUTES } from "./menuTrail";
import { MenuIcon, UI_ICONS } from "./menuIcons";

const useStyles = makeStyles((theme) => {
  const mode = theme.palette.type;
  const brand = theme.palette.primary.main;
  const t = getTokens(mode, brand);
  // Tinta e destaque do menu vêm das duas cores da empresa (App.js → tokens.nav)
  const nav = (theme.tokens && theme.tokens.nav) || t.nav;

  return {
    navRoot: {
      width: "100%",
      minWidth: 0,
      padding: "0 0 10px",
      color: nav.text,
      "& > li": {
        listStyle: "none",
      },
    },

    navRootCollapsed: {
      "& $listItem": {
        width: 44,
        minWidth: 44,
        height: 44,
        minHeight: 44,
        margin: "0 auto 4px",
        padding: 0,
        justifyContent: "center",
      },
      "& $listItem .MuiListItemIcon-root": {
        minWidth: 0,
        width: "100%",
        justifyContent: "center",
      },
      "& $listItemText, & $expandIcon": {
        display: "none",
      },
      "& $submenuContainer": {
        display: "none",
      },
      "& $groupSpacer": {
        height: 6,
      },
      "& $groupDivider": {
        width: 20,
        margin: "12px auto",
      },
    },

    // Item do painel: 48px, ícone 22px, raio 8, texto branco a 68%
    listItem: {
      position: "relative",
      minHeight: 48,
      width: "auto",
      borderRadius: t.radius.sm,
      margin: "0 0 4px",
      padding: "0 12px",
      color: nav.text,
      overflow: "hidden",
      transition: "background-color 200ms ease, color 200ms ease",
      "&:hover": {
        color: nav.textStrong,
        backgroundColor: nav.hover,
      },
      "&:focus-visible": {
        outline: `2px solid ${nav.accent}`,
        outlineOffset: -2,
      },
      "& .MuiListItemIcon-root": {
        minWidth: 32,
        color: "inherit",
      },
      "& .MuiListItemText-root": {
        margin: 0,
      },
    },

    listItemText: {
      fontSize: 14,
      lineHeight: 1.4,
      color: "inherit",
      fontWeight: 400,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontFamily: t.font,
    },

    avatarActive: {
      backgroundColor: "transparent",
    },

    avatarHover: {
      backgroundColor: "transparent",
    },

    // Ícone solto, sem caixa; `overflow: visible` para o ponto de não lidas não ser cortado
    iconHoverActive: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 0,
      height: 22,
      width: 22,
      overflow: "visible",
      backgroundColor: "transparent",
      color: "inherit",
      transition: "color 200ms ease",
      "&.active": {
        color: nav.accent,
      },
      fontSize: 23,
      "& svg": {
        display: "block",
        flex: "none",
      },
    },

    // Contador: erro sólido com texto branco, sem pulse
    badge: {
      "& .MuiBadge-badge": {
        backgroundColor: theme.palette.error.main,
        color: "#fff",
        fontSize: "0.7rem",
        fontWeight: 600,
        minWidth: 16,
        height: 16,
        padding: "0 4px",
      },
    },

    // Ponto de não lidas do chat interno
    unreadDot: {
      "& .MuiBadge-dot": {
        backgroundColor: theme.palette.error.main,
      },
    },

    // Subitens: mesmos itens, mais baixos e recuados (o painel de origem não tem submenu)
    submenuContainer: {
      margin: "0 0 6px",
      paddingLeft: 12,
      "& $listItem": {
        minHeight: 40,
        margin: "0 0 2px",
        padding: "0 10px",
      },
      "& $listItem .MuiListItemIcon-root": {
        minWidth: 28,
      },
      "& $iconHoverActive": {
        height: 20,
        width: 20,
        fontSize: 20,
      },
      "& $listItemText": {
        fontSize: 13,
      },
    },

    menuSearch: {
      width: "100%",
      margin: "2px 0 16px",
      "& .MuiOutlinedInput-root": {
        height: 38,
        borderRadius: t.radius.sm,
        backgroundColor: "rgba(255,255,255,0.06)",
        color: nav.textStrong,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: nav.line,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.26)",
        },
        "&.Mui-focused": {
          backgroundColor: "rgba(255,255,255,0.09)",
          boxShadow: `0 0 0 3px ${nav.accentSoft}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: nav.accent,
        },
      },
      "& .MuiOutlinedInput-input": {
        color: nav.textStrong,
        fontSize: 13.5,
        "&::placeholder": {
          color: nav.textMuted,
          opacity: 1,
        },
      },
      "& .MuiInputAdornment-root": {
        color: nav.textMuted,
        fontSize: 17,
      },
    },

    groupTitle: {
      padding: "0 12px",
      margin: "0 0 8px",
      fontSize: 10.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      lineHeight: "16px",
      color: nav.textMuted,
      whiteSpace: "nowrap",
      overflow: "hidden",
    },

    groupSpacer: {
      height: 14,
    },

    groupDivider: {
      height: 1,
      margin: "12px 12px",
      backgroundColor: nav.line,
    },

    versionContainer: {
      textAlign: "center",
      padding: "12px 12px 4px",
      color: nav.textMuted,
      fontSize: 10.5,
      fontWeight: 500,
      letterSpacing: ".04em",
    },

    // margem negativa: a seta entra no padding para o rótulo do grupo não cortar
    expandIcon: {
      color: nav.textMuted,
      fontSize: 14,
      marginRight: -2,
      flexShrink: 0,
      transition: "transform 180ms ease",
    },
    expandIconOpen: {
      transform: "rotate(180deg)",
    },

    // Menu recolhido: o grupo abre num balão ao lado, na mesma tinta do menu
    flyoutPaper: {
      marginLeft: 14,
      minWidth: 236,
      maxWidth: 280,
      padding: 8,
      borderRadius: 12,
      color: nav.text,
      backgroundColor: nav.bg,
      backgroundImage: "linear-gradient(rgba(255,255,255,0.045), rgba(255,255,255,0.045))",
      border: `1px solid ${nav.line}`,
      boxShadow: "0 18px 48px rgba(2, 8, 20, 0.42), 0 2px 8px rgba(2, 8, 20, 0.28)",
      overflow: "visible",
      // setinha apontando para o ícone do grupo
      "&::before": {
        content: '""',
        position: "absolute",
        top: 17,
        left: -6,
        width: 10,
        height: 10,
        transform: "rotate(45deg)",
        backgroundColor: nav.bg,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.045), rgba(255,255,255,0.045))",
        borderLeft: `1px solid ${nav.line}`,
        borderBottom: `1px solid ${nav.line}`,
      },
      "& $listItem": {
        minHeight: 40,
        margin: "0 0 2px",
        padding: "0 10px",
      },
      "& $listItem:last-child, & li:last-child $listItem": {
        marginBottom: 0,
      },
      "& $listItem .MuiListItemIcon-root": {
        minWidth: 30,
      },
      "& $iconHoverActive": {
        height: 20,
        width: 20,
        fontSize: 20,
      },
      "& $listItemText": {
        fontSize: 13.5,
      },
    },
    flyoutTitle: {
      margin: 0,
      padding: "6px 10px 9px",
      fontFamily: t.font,
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.08em",
      lineHeight: "16px",
      textTransform: "uppercase",
      color: nav.textMuted,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // Ativo do painel: fundo translúcido do destaque + barra interna de 3px
    activeItem: {
      color: nav.accentText,
      backgroundColor: nav.accentSoft,
      boxShadow: `inset 3px 0 ${nav.accent}`,
      "&:hover": {
        color: nav.accentText,
        backgroundColor: nav.accentSoftHover,
      },
      "& $iconHoverActive": {
        color: nav.accent,
      },
      "& $listItemText": {
        fontWeight: 600,
      },
    },

    // Grupo que contém a tela atual: só realce de texto e ícone — a barra fica no subitem
    activeGroup: {
      color: nav.textStrong,
      "& $iconHoverActive": {
        color: nav.accent,
      },
      "& $listItemText": {
        fontWeight: 600,
      },
    },
  };
});

// Título de grupo: texto quando expandido, linha fina quando colapsado
function GroupTitle({ label, collapsed }) {
  const classes = useStyles();
  if (collapsed) return <Divider className={classes.groupDivider} />;
  return <Typography className={classes.groupTitle}>{label}</Typography>;
}

const NavigationSearchContext = createContext("");
// true para os itens renderizados dentro do balão do menu recolhido
const FlyoutContext = createContext(false);
// estado dos grupos, compartilhado com MenuGroup (componente estável, fora do MainListItems)
const MenuGroupContext = createContext(null);

// Grupo do menu. Expandido: abre/fecha em sanfona. Recolhido: abre os subitens num balão ao lado.
function MenuGroup({ id, label, icon, children }) {
  const classes = useStyles();
  const { collapsed, searching, isOpen, toggleGroup, activeGroup } = useContext(MenuGroupContext);
  const open = searching || isOpen(id);
  const active = activeGroup === id;
  const [hover, setHover] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const flyoutOpen = collapsed && Boolean(anchorEl);

  // expandiu o menu com o balão aberto: fecha
  useEffect(() => {
    if (!collapsed) setAnchorEl(null);
  }, [collapsed]);

  const handleClick = (event) => {
    if (collapsed) setAnchorEl(event.currentTarget);
    else toggleGroup(id);
  };

  return (
    <div className="digite-menu-group" data-searching={searching}>
      <Tooltip title={collapsed && !flyoutOpen ? label : ""} placement="right">
        <ListItem
          dense
          button
          className={`${classes.listItem} ${active ? classes.activeGroup : ""}`}
          onClick={handleClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          aria-expanded={collapsed ? flyoutOpen : open}
          aria-haspopup={collapsed ? "menu" : undefined}
        >
          <ListItemIcon>
            <Avatar className={`${classes.iconHoverActive} ${active || hover || flyoutOpen ? "active" : ""}`}>{icon}</Avatar>
          </ListItemIcon>
          <ListItemText primary={<Typography className={classes.listItemText}>{label}</Typography>} />
          <UI_ICONS.caretDown className={`${classes.expandIcon} ${open ? classes.expandIconOpen : ""}`} aria-hidden="true" />
        </ListItem>
      </Tooltip>
      {collapsed ? (
        <Popover
          open={flyoutOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ className: classes.flyoutPaper, elevation: 0 }}
        >
          <p className={classes.flyoutTitle}>{label}</p>
          <FlyoutContext.Provider value={true}>
            <List
              dense
              component="div"
              disablePadding
              onClick={(event) => {
                if (event.target.closest("a")) setAnchorEl(null);
              }}
            >
              {children}
            </List>
          </FlyoutContext.Provider>
        </Popover>
      ) : (
        <Collapse in={open} timeout="auto" unmountOnExit className={classes.submenuContainer}>
          <List dense component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      )}
    </div>
  );
}
const navigationAliases = {
  "/connections": "conexões conexao whatsapp canais channels",
  "/flowbuilders": "chatbot bot fluxos automação",
  "/messages-api": "api token chave integração requisição",
  "/users": "usuários equipe atendentes team",
  "/settings": "configurações preferências horários settings",
  "/queues": "filas setores atendimento queues",
};

function ListItemLink(props) {
  const { icon, primary, to, showBadge } = props;
  const inFlyout = useContext(FlyoutContext);
  const tooltip = props.tooltip && !inFlyout;
  const search = useContext(NavigationSearchContext);
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive =
    activeMenu === to ||
    location.pathname === to ||
    (to !== "/" && location.pathname.startsWith(`${to}/`));

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  if (search && !normalizeSearch(`${primary} ${navigationAliases[to] || ""}`).includes(normalizeSearch(search))) return null;

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem button component={renderLink} className={`${classes.listItem} ${isActive ? classes.activeItem : ""}`}>
          {icon ? (
            <ListItemIcon>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                      }`}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                    }`}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {primary}
              </Typography>
            }
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);


  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);
  const { enabled: hotspotEnabled } = useHotspotStatus();


  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        setShowWallets(setting.DirectTicketsToWallets);

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);



  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      console.log('Registrando listener para:', eventName);

      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          console.log('Removendo listener para:', eventName);
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          if (whats.channel === "webchat") return false;
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  // ---- Grupos recolhíveis: estado lembrado por usuário; o grupo da rota ativa abre sozinho ----
  const [menuSearch, setMenuSearch] = useState("");
  const menuStorageKey = `digiteai:menu:${user?.id || "anon"}`;
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(menuStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });
  const toggleGroup = (id) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(menuStorageKey, JSON.stringify(next)); } catch (e) { /* ignora */ }
      return next;
    });
  const routeIn = (prefixes) =>
    prefixes.some((p) => (p === "/" ? location.pathname === "/" : location.pathname === p || location.pathname.startsWith(`${p}/`)));
  const activeGroup = Object.keys(GROUP_ROUTES).find((g) => routeIn(GROUP_ROUTES[g])) || "";
  useEffect(() => {
    if (activeGroup) setOpenGroups(previous => ({ ...previous, [activeGroup]: true }));
  }, [activeGroup]);
  const isOpen = (id) => openGroups[id] === true || (openGroups[id] === undefined && activeGroup === id);

  // Integrações de ERP da empresa (SGP/IXC/Asaas) — decide se o grupo "Cobrança & Provedor" aparece
  const [erpTypes, setErpTypes] = useState([]);
  useEffect(() => {
    let active = true;
    api.get("/isp/erp-integrations")
      .then(({ data }) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : data?.integrations || [];
        setErpTypes(list.map((it) => String(it.type || "").toUpperCase()));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [user.companyId]);
  const hasErp = erpTypes.some((t) => ["SGP", "IXC", "ASAAS"].includes(t));
  const hasTopologyErp = erpTypes.some((t) => ["SGP", "IXC"].includes(t));

  const isAdmin = user.profile === "admin";
  const canSeeManagement =
    isAdmin || (user.profile === "user" && (user.showDashboard === "enabled" || user.allowRealTime === "enabled"));
  const canSeeCampaigns = user?.showCampaign === "enabled" && showCampaigns;
  const canSeeBilling =
    (hasErp || hotspotEnabled) && (isAdmin || user?.showCampaign === "enabled");
  const canSeeAutomation =
    user.showFlow === "enabled" || isAdmin;
  const canSeeConfig = isAdmin || (user.profile === "user" && user.allowConnections === "enabled");
  const canSeePlatform = !!user.super || canManageTenants(user);

  const menuGroupContext = {
    collapsed: Boolean(collapsed),
    searching: Boolean(menuSearch) && !collapsed,
    isOpen,
    toggleGroup,
    activeGroup,
  };

  return (
    <NavigationSearchContext.Provider value={collapsed ? "" : menuSearch}>
    <MenuGroupContext.Provider value={menuGroupContext}>
    <style>{`.digite-menu-group[data-searching="true"]:not(:has(a)){display:none}`}</style>
    <div
      className={`${classes.navRoot} ${collapsed ? classes.navRootCollapsed : ""}`}
      onClick={(event) => {
        if (event.target.closest("a")) { setMenuSearch(""); drawerClose?.(); }
      }}
      role="navigation"
      aria-label="Navegação principal"
    >
      {!collapsed && <TextField value={menuSearch} onChange={event => setMenuSearch(event.target.value)} placeholder={i18n.t("mainDrawer.listItems.searchMenu")} type="search" variant="outlined" size="small" className={classes.menuSearch} inputProps={{ "aria-label": i18n.t("mainDrawer.listItems.searchMenu") }} InputProps={{ startAdornment: <InputAdornment position="start"><UI_ICONS.search /></InputAdornment> }} />}
      {/* 1. Atendimento — sempre visível, sem submenu */}
      <GroupTitle label={i18n.t("mainDrawer.listItems.attendance")} collapsed={collapsed} />
      <ListItemLink to="/tickets" primary={i18n.t("mainDrawer.listItems.tickets")} icon={<MenuIcon name="/tickets" />} tooltip={collapsed} />
      {user.showContacts === "enabled" && (
        <ListItemLink to="/contacts" primary={i18n.t("mainDrawer.listItems.contacts")} icon={<MenuIcon name="/contacts" />} tooltip={collapsed} />
      )}
      {showKanban && (
        <ListItemLink to="/kanban" primary={i18n.t("mainDrawer.listItems.kanban")} icon={<MenuIcon name="/kanban" />} tooltip={collapsed} />
      )}
      {showInternalChat && (
        <ListItemLink
          to="/chats"
          primary={i18n.t("mainDrawer.listItems.chats")}
          icon={
            <Badge color="error" variant="dot" invisible={invisible} className={classes.unreadDot}>
              <MenuIcon name="/chats" />
            </Badge>
          }
          tooltip={collapsed}
        />
      )}
      {showSchedules && (
        <ListItemLink to="/schedules" primary={i18n.t("mainDrawer.listItems.schedules")} icon={<MenuIcon name="/schedules" />} tooltip={collapsed} />
      )}
      <ListItemLink to="/quick-messages" primary={i18n.t("mainDrawer.listItems.quickMessages")} icon={<MenuIcon name="/quick-messages" />} tooltip={collapsed} />
      <ListItemLink to="/tags" primary={i18n.t("mainDrawer.listItems.tags")} icon={<MenuIcon name="/tags" />} tooltip={collapsed} />

      {/* 2. Gestão */}
      {canSeeManagement && (
        <>
          <div className={classes.groupSpacer} />
          <MenuGroup id="management" label={i18n.t("mainDrawer.listItems.management")} icon={<MenuIcon name="management" />}>
            <Can
              role={user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
              perform={"drawer-admin-items:view"}
              yes={() => (
                <ListItemLink to="/" primary={i18n.t("mainDrawer.listItems.dashboard")} icon={<MenuIcon name="/" />} tooltip={collapsed} />
              )}
            />
            <Can
              role={user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
              perform={"drawer-admin-items:view"}
              yes={() => (
                <>
                  <ListItemLink to="/moments" primary={i18n.t("mainDrawer.listItems.chatsTempoReal")} icon={<MenuIcon name="/moments" />} tooltip={collapsed} />
                  <ListItemLink to="/supervision" primary={i18n.t("mainDrawer.listItems.supervision")} icon={<MenuIcon name="/supervision" />} tooltip={collapsed} />
                </>
              )}
            />
            <Can
              role={user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
              perform={"drawer-admin-items:view"}
              yes={() => (
                <>
                  <ListItemLink to="/reports" primary={i18n.t("mainDrawer.listItems.reports")} icon={<MenuIcon name="/reports" />} tooltip={collapsed} />
                  <ListItemLink to="/ratings" primary={i18n.t("mainDrawer.listItems.ratings")} icon={<MenuIcon name="/ratings" />} tooltip={collapsed} />
                  {isAdmin && <ListItemLink to="/attendance-insights" primary={i18n.t("mainDrawer.listItems.attendanceInsights")} icon={<MenuIcon name="/attendance-insights" />} tooltip={collapsed} />}
                </>
              )}
            />
            {isAdmin && showWallets && (
              <ListItemLink to="/wallets" primary={i18n.t("mainDrawer.listItems.wallets")} icon={<MenuIcon name="/wallets" />} tooltip={collapsed} />
            )}
            {showWavoipCall && (
              <ListItemLink to="/call-historicals" primary={i18n.t("mainDrawer.listItems.callHistorical")} icon={<MenuIcon name="/call-historicals" />} tooltip={collapsed} />
            )}
          </MenuGroup>
        </>
      )}

      {/* 3. Campanhas */}
      {canSeeCampaigns && (
        <MenuGroup id="campaigns" label={i18n.t("mainDrawer.listItems.campaigns")} icon={<MenuIcon name="campaigns" />}>
          <ListItemLink to="/campaigns" primary={i18n.t("campaigns.subMenus.list")} icon={<MenuIcon name="/campaigns" />} tooltip={collapsed} />
          <ListItemLink to="/contact-lists" primary={i18n.t("campaigns.subMenus.listContacts")} icon={<MenuIcon name="/contact-lists" />} tooltip={collapsed} />
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <ListItemLink to="/files" primary={i18n.t("mainDrawer.listItems.files")} icon={<MenuIcon name="/files" />} tooltip={collapsed} />
            )}
          />
          <ListItemLink to="/campaigns-config" primary={i18n.t("campaigns.subMenus.settings")} icon={<MenuIcon name="/campaigns-config" />} tooltip={collapsed} />
        </MenuGroup>
      )}

      {/* 4. Cobrança & Provedor — só com ERP (SGP/IXC/Asaas) ou Hotspot */}
      {canSeeBilling && (
        <MenuGroup id="billing" label={i18n.t("mainDrawer.listItems.billingGroup")} icon={<MenuIcon name="billing" />}>
          {hasErp && (
            <>
              <ListItemLink to="/billing-rules" primary={i18n.t("campaigns.subMenus.billingRules")} icon={<MenuIcon name="/billing-rules" />} tooltip={collapsed} />
              <ListItemLink to="/invoice-campaigns" primary={i18n.t("campaigns.subMenus.invoiceCampaigns")} icon={<MenuIcon name="/invoice-campaigns" />} tooltip={collapsed} />
            </>
          )}
          {hasTopologyErp && (
            <ListItemLink to="/network-alerts" primary={i18n.t("mainDrawer.listItems.networkAlerts")} icon={<MenuIcon name="/network-alerts" />} tooltip={collapsed} />
          )}
          {hasTopologyErp && isAdmin && (
            <ListItemLink to="/erp-writeback" primary={i18n.t("campaigns.subMenus.erpWriteback")} icon={<MenuIcon name="/erp-writeback" />} tooltip={collapsed} />
          )}
          {hotspotEnabled && (
            <ListItemLink to="/hotspot" primary={i18n.t("mainDrawer.listItems.hotspot")} icon={<MenuIcon name="/hotspot" />} tooltip={collapsed} />
          )}
        </MenuGroup>
      )}

      {/* 5. Automação */}
      {canSeeAutomation && (
        <MenuGroup id="automation" label={i18n.t("mainDrawer.listItems.automation")} icon={<MenuIcon name="automation" />}>
          {isAdmin && <ListItemLink to="/birthday-settings" primary={i18n.t("mainDrawer.listItems.birthdaySettings")} icon={<MenuIcon name="/birthday-settings" />} tooltip={collapsed} />}
          {user.showFlow === "enabled" && (
            <>
              <ListItemLink to="/flowbuilders" primary={i18n.t("mainDrawer.listItems.flowbuilders")} icon={<MenuIcon name="/flowbuilders" />} tooltip={collapsed} />
              <ListItemLink to="/phrase-lists" primary={i18n.t("mainDrawer.listItems.phraseLists")} icon={<MenuIcon name="/phrase-lists" />} tooltip={collapsed} />
            </>
          )}
          {showOpenAi && (
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink to="/prompts" primary={i18n.t("mainDrawer.listItems.prompts")} icon={<MenuIcon name="/prompts" />} tooltip={collapsed} />
              )}
            />
          )}
          {isAdmin && (
            <ListItemLink to="/ai-learning" primary={i18n.t("mainDrawer.listItems.aiLearning")} icon={<MenuIcon name="/ai-learning" />} tooltip={collapsed} />
          )}
        </MenuGroup>
      )}

      {canSeeConfig && <MenuGroup id="channels" label={i18n.t("mainDrawer.listItems.channelsGroup")} icon={<MenuIcon name="channels" />}>
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform={"drawer-admin-items:view"}
            yes={() => (
              <ListItemLink to="/connections" primary={i18n.t("mainDrawer.listItems.connections")} icon={<MenuIcon name="/connections" />} showBadge={connectionWarning} tooltip={collapsed} />
            )}
          />
          <Can role={user.profile} perform="dashboard:view" yes={() => <>
                {showExternalApi && (
                  <ListItemLink to="/messages-api" primary={i18n.t("mainDrawer.listItems.messagesAPI")} icon={<MenuIcon name="/messages-api" />} tooltip={collapsed} />
                )}
          {showIntegrations && (
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink to="/queue-integration" primary={i18n.t("mainDrawer.listItems.queueIntegration")} icon={<MenuIcon name="/queue-integration" />} tooltip={collapsed} />
              )}
            />
          )}
          {showCampaigns && isAdmin && (
            <ListItemLink to="/meta-templates" primary={i18n.t("campaigns.subMenus.metaTemplates")} icon={<MenuIcon name="/meta-templates" />} tooltip={collapsed} />
          )}
          </>} />
      </MenuGroup>}

      {/* 6. Configurações */}
      {isAdmin && (
        <MenuGroup id="config" label={i18n.t("mainDrawer.listItems.configGroup")} icon={<MenuIcon name="config" />}>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <>
                <ListItemLink to="/queues" primary={i18n.t("mainDrawer.listItems.queues")} icon={<MenuIcon name="/queues" />} tooltip={collapsed} />
                <ListItemLink to="/users" primary={i18n.t("mainDrawer.listItems.users")} icon={<MenuIcon name="/users" />} tooltip={collapsed} />
                <ListItemLink to="/financeiro" primary={i18n.t("mainDrawer.listItems.financeiro")} icon={<MenuIcon name="/financeiro" />} tooltip={collapsed} />
                <ListItemLink to="/settings" primary={i18n.t("mainDrawer.listItems.settings")} icon={<MenuIcon name="/settings" />} tooltip={collapsed} />
              </>
            )}
          />
        </MenuGroup>
      )}

      {/* 7. Ajuda */}
      {hasHelps && (
        <>
          <div className={classes.groupSpacer} />
          <ListItemLink to="/helps" primary={i18n.t("mainDrawer.listItems.helps")} icon={<MenuIcon name="/helps" />} tooltip={collapsed} />
        </>
      )}

      {/* Plataforma — só super / revendedor */}
      {canSeePlatform && (
        <>
          <div className={classes.groupSpacer} />
          <GroupTitle label={i18n.t("mainDrawer.listItems.platform")} collapsed={collapsed} />
          {canManageTenants(user) && (
            <>
              <ListItemLink to="/companies" primary={i18n.t("mainDrawer.listItems.companies")} icon={<MenuIcon name="/companies" />} tooltip={collapsed} />
              <ListItemLink to="/reseller-finance" primary={i18n.t("mainDrawer.listItems.resellerFinance")} icon={<MenuIcon name="/reseller-finance" />} tooltip={collapsed} />
            </>
          )}
          {user.super && (
            <>
              <ListItemLink to="/allConnections" primary={i18n.t("mainDrawer.listItems.allConnections")} icon={<MenuIcon name="/allConnections" />} tooltip={collapsed} />
              <ListItemLink to="/announcements" primary={i18n.t("mainDrawer.listItems.annoucements")} icon={<MenuIcon name="/announcements" />} tooltip={collapsed} />
              <ListItemLink to="/ai-platform" primary={i18n.t("mainDrawer.listItems.aiPlatform")} icon={<MenuIcon name="/ai-platform" />} tooltip={collapsed} />
              <ListItemLink to="/system-resources" primary={i18n.t("mainDrawer.listItems.systemResources")} icon={<MenuIcon name="/system-resources" />} tooltip={collapsed} />
            </>
          )}
        </>
      )}

      {!collapsed && (
        <Typography className={classes.versionContainer}>
          {version ? `v${String(version).replace(/^v/i, "")}` : ""}
        </Typography>
      )}
    </div>
    </MenuGroupContext.Provider>
    </NavigationSearchContext.Provider>
  );
};

export default MainListItems;
